// Purchased Products Section - User's purchased products in grid, ordered by purchase date
// Follows the Section, Component & Manager System architecture

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { PurchasedProductsManager } from './managers/purchased-products-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { ProductSearchBar, ProductGrid, ProductPagination } from '../../components';
import { eventUtils } from '../../../../core/utils';
import './PurchasedProductsSection.css';
import '../../ProductsSection.css';

export function PurchasedProductsSection() {
  const { manager, isInitialized } = useManager(PurchasedProductsManager);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = 25;
  const urlQuery = searchParams.get('query') || '';

  const hasInitializedRef = React.useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && urlQuery) {
      hasInitializedRef.current = true;
      setLocalSearchTerm(urlQuery);
      setCurrentSearchTerm(urlQuery);
    }
  }, [urlQuery]);

  const shouldShowSearchResults = currentSearchTerm.trim().length > 0;

  const loadPurchasedProducts = useCallback(async (query, page = 1) => {
    if (!user) return;

    setIsLoading(true);
    try {
      const response = await manager.getPurchasedProductsFromAPI(page, itemsPerPage, query || '');
      if (response.unauthorized) {
        setProducts([]);
        setTotal(0);
      } else {
        setProducts(response.products || []);
        setTotal(response.total || 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [manager, user]);

  const performSearch = useCallback(async (query) => {
    const processedQuery = query.trim();
    setCurrentSearchTerm(processedQuery);
    setCurrentPage(1);
    await loadPurchasedProducts(processedQuery, 1);
  }, [loadPurchasedProducts]);

  useEffect(() => {
    if (!isInitialized || !user) return;
    const query = shouldShowSearchResults ? currentSearchTerm : '';
    loadPurchasedProducts(query, currentPage);
  }, [isInitialized, user, currentPage, shouldShowSearchResults, currentSearchTerm, loadPurchasedProducts]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    if (shouldShowSearchResults) {
      loadPurchasedProducts(currentSearchTerm, page);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [shouldShowSearchResults, currentSearchTerm, loadPurchasedProducts]);

  const handleProductsClick = useCallback((e) => {
    e.preventDefault();
    navigate('/marketplace');
  }, [navigate]);

  const handleProductClick = useCallback((product) => {
    if (product?.id) {
      navigate(`/marketplace/details?productId=${product.id}`);
    }
  }, [navigate]);

  const updateUrlFromTyping = useCallback((query) => {
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      newParams.set('query', query);
    } else {
      newParams.delete('query');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const debouncedUrlUpdateRef = React.useRef(null);
  useEffect(() => {
    debouncedUrlUpdateRef.current = eventUtils.debounce(updateUrlFromTyping, 250);
  }, [updateUrlFromTyping]);

  const debouncedUrlUpdate = useCallback((...args) => {
    if (debouncedUrlUpdateRef.current) {
      debouncedUrlUpdateRef.current(...args);
    }
  }, []);

  const handleSearchChange = useCallback((newTerm) => {
    setLocalSearchTerm(newTerm);
    debouncedUrlUpdate(newTerm);
  }, [debouncedUrlUpdate]);

  if (!user) {
    return (
      <MainLayout contentClassName="main-content--product-details">
        <div className="purchased-products-section">
          <div className="product-details-search-row">
            <div className="product-details-controls-row">
              <button
                type="button"
                className="purchased-products-back-btn"
                onClick={handleProductsClick}
                title="Back to Marketplace"
              >
                <i className="fa-solid fa-arrow-left"></i>
                Back to Marketplace
              </button>
              <span className="purchased-products-title">My Purchases</span>
            </div>
          </div>
          <div className="purchased-products-sign-in">
            <i className="fa-solid fa-lock"></i>
            <h3>Sign in to view your purchases</h3>
            <p>Your purchased products will appear here once you sign in.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--product-details">
      <div className="purchased-products-section">
        <div className="product-details-search-row">
          <div className="product-details-controls-row">
            <button
              type="button"
              className="purchased-products-back-btn"
              onClick={handleProductsClick}
              title="Back to Products"
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to Products
            </button>
            <span className="purchased-products-title">My Purchases</span>
            <ProductSearchBar
              searchTerm={localSearchTerm}
              onSearchChange={handleSearchChange}
              onSubmit={performSearch}
              placeholder="Search your purchases..."
            />
          </div>
        </div>

        <div className="purchased-products-content">
          <ProductPagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />

          <div className="products-grid-area">
            <ProductGrid
              products={products}
              onProductClick={handleProductClick}
              loading={!isInitialized || isLoading}
              emptyMessage={
                shouldShowSearchResults
                  ? "No purchased products match your search"
                  : "You haven't purchased any products yet"
              }
              admin={false}
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
